import { Injectable } from '@angular/core';


import {
  HttpEvent,
  HttpInterceptor,
  HttpHandler,
  HttpRequest,
  HttpErrorResponse,
} from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { HotToastService } from '@ngxpert/hot-toast';
import { environment } from '../../../environments/environment';
import { Router } from '@angular/router';
import { ServerErrorGuard } from '../auth-guard/serverError/server-error-guard';

@Injectable()
export class ErrorInterceptor implements HttpInterceptor {
  constructor(
    private toastr: HotToastService,
    private router: Router,
    private serverErrorGuard: ServerErrorGuard,
  ) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    // Skip version.json and static assets
    if (
      request.url.includes('version.json') ||
      request.url.endsWith('.js') ||
      request.url.endsWith('.css') ||
      request.url.includes('assets')
    ) {
      return next.handle(request);
    }

    return next.handle(request).pipe(
      catchError((error: HttpErrorResponse | Error) => {
        // FIX 4: Don't silently swallow non-error responses; only intercept real errors.
        if (error instanceof HttpErrorResponse && error.status < 400) {
          return throwError(() => error);
        }

        const message = this.handleErrors(error);
        return throwError(() => new Error(message));
      }),
    );
  }

 
private handleErrors(error: HttpErrorResponse | Error): string {
  const defaultErrorMessage =
    'Something went wrong. Please try again later.';

  let message = defaultErrorMessage;

  if (error instanceof HttpErrorResponse) {
    const { status, url, error: errorPayload } = error;

    // Network or connection error
    if (status === 0) {
      message =
        'Unable to connect to the server. Please check your internet connection and try again.';

      this.showError(message);
      console.error('Network error:', error);
      return message;
    }

    const payload = errorPayload?.payload || errorPayload;

    // Log technical details only in development
    if (!environment.production) {
      console.group(`API Error Details - ${url}`);
      console.error('Status:', status);
      console.error('URL:', url);
      console.error('Payload:', payload);
      console.groupEnd();
    }

    switch (status) {
      case 400:
        message =
          this.handleBadRequest(error) ??
          'The request could not be processed. Please check your information.';
        this.showError(message);
        break;

      case 401:
        message =
          this.handleBadRequest(error) ??
          'Your session has expired. Please sign in again to continue.';

        this.showError(message);
        localStorage.clear();
        this.router.navigate(['/signin']);
        break;

      case 403:
        message =
          'You don’t have permission to perform this action.';
        this.showError(message);
        break;

      case 404:
        message =
          'The requested resource could not be found.';
        this.showError(message);
        break;

      case 502:
        message =
          'The server is temporarily unavailable. Please try again later.';
        this.serverErrorGuard.allow();
        this.router.navigate(['/server-error']);
        break;

      case 503:
        message =
          'The service is temporarily unavailable. Please try again later.';
        this.showError(message);
        break;

      case 504:
        message =
          'The server took too long to respond. Please try again later.';
        this.showError(message);
        break;

      default:
        message = defaultErrorMessage;
        this.showError(message);
        break;
    }
  } else {
    message = defaultErrorMessage;
    this.showError(message);

    if (!environment.production) {
      console.error('Unexpected application error:', error);
    }
  }

  return message;
}


  private handleBadRequest(error: HttpErrorResponse): string | undefined {
    const validationResult = error.error?.metaData?.validationResult;
    const exceptionMessage = error.error?.metaData?.exceptionMessage;

    if (validationResult?.length > 0) {
      return (
        validationResult.map((v: { errorMessage: string }) => v.errorMessage).join('\n') ||
        'Validation error(s) occurred.'
      );
    }

    // FIX 2: Return the exceptionMessage so callers can use it (showError is
    // now handled by the caller, not here, to avoid double-toasting).
    if (exceptionMessage) {
      return exceptionMessage;
    }

    const metaMessage = error.error?.metaData?.message;
    if (metaMessage) {
      return metaMessage;
    }

    return undefined;
  }

  private showError(message: string): void {
    this.toastr.error(message, { position: 'top-right' });
  }
}